import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/doctocat/doctocat/theme/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`This guide will walk you through creating, customizing, and deploying a new site with Doctocat using our `}<a parentName="p" {...{
        "href": "https://github.com/primer/doctocat-template"
      }}>{`template repository`}</a>{`. If you have an existing repository where you would like to create a new site, we recommend using the Gatsby CLI instead. Check out the `}<a parentName="p" {...{
        "href": "/getting-started/gatsby-cli"
      }}>{`Gatsby CLI guide`}</a>{` for more information.`}</p>
    <h2>{`1. Create a new repository from the template`}</h2>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://github.com/primer/doctocat-starter"
      }}>{`Doctocat template`}</a>{` to create a new repository. See "`}<a parentName="p" {...{
        "href": "https://help.github.com/en/articles/creating-a-repository-from-a-template"
      }}>{`Creating a repository from a template`}</a>{`" for more information about using template repositories.`}</p>
    <p><img parentName="p" {...{
        "src": "https://help.github.com/assets/images/help/repository/use-this-template-button.png",
        "alt": "Use this template button"
      }}></img></p>
    <p>{`Let's check out the directory structure of your new repository:`}</p>
    <pre><code parentName="pre" {...{}}>{`.
├── gatsby-config.js
├── content/
├── src/
│   └── @primer/
│       └── gatsby-theme-doctocat
├── package.json
└── now.json
`}</code></pre>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`gatsby-config.js`}</inlineCode>{`: This is your `}<a parentName="li" {...{
          "href": "https://www.gatsbyjs.org/docs/gatsby-config/"
        }}>{`Gatsby configuration`}</a>{`, where you can specify information about your site (such as title and description), and any additional `}<a parentName="li" {...{
          "href": "https://www.gatsbyjs.org/plugins/"
        }}>{`Gatsby plugins`}</a>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content/`}</inlineCode>{`: This is where you will spend most of your time writing documentation. Markdown files (`}<inlineCode parentName="li">{`.md`}</inlineCode>{` or `}<inlineCode parentName="li">{`.mdx`}</inlineCode>{`) in `}<inlineCode parentName="li">{`content/`}</inlineCode>{` will be interpreted as `}<a parentName="li" {...{
          "href": "https://mdxjs.com/"
        }}>{`MDX`}</a>{`, a combination of standard Markdown and `}<a parentName="li" {...{
          "href": "https://reactjs.org/docs/jsx-in-depth.html"
        }}>{`React JSX`}</a>{`. Each document `}<a parentName="li" {...{
          "href": "https://www.gatsbyjs.org/docs/recipes/#creating-pages"
        }}>{`becomes a page`}</a>{` on your site with a URL based on its path relative to `}<inlineCode parentName="li">{`content/`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`src/`}</inlineCode>{`: This directory will contain any supporting files such as assets and custom React components.`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`@primer/gatsby-theme-doctocat/`}</inlineCode>{`: You can use this directory to customize the look and behavior of the theme. All files placed in this directory will "shadow" files in the `}<inlineCode parentName="li">{`src`}</inlineCode>{` directory of the Doctocat theme. Check out "`}<a parentName="li" {...{
              "href": "https://www.gatsbyjs.org/blog/2019-04-29-component-shadowing/"
            }}>{`What is Component Shadowing?`}</a>{`" for more information.`}</li>
        </ul></li>
      <li parentName="ul"><inlineCode parentName="li">{`package.json`}</inlineCode>{`: This file contains metadata like the project's name, repository, and a list of packages the project depends on. Check out the `}<a parentName="li" {...{
          "href": "https://docs.npmjs.com/files/package.json"
        }}>{`package.json docs`}</a>{` for more information.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`now.json`}</inlineCode>{`: When using `}<a parentName="li" {...{
          "href": "https://zeit.co/now"
        }}>{`Now`}</a>{` to deploy, the configuration for your deployment is read from this file. Check out the `}<a parentName="li" {...{
          "href": "https://zeit.co/docs/v2/deployments/configuration/?query=now.json"
        }}>{`now.json docs`}</a>{` for more information. Feel free to delete this file if you don't plan to deploy your site or want to use a different deployment service, like `}<a parentName="li" {...{
          "href": "https://www.netlify.com/"
        }}>{`Netlify`}</a>{` or `}<a parentName="li" {...{
          "href": "https://surge.sh/"
        }}>{`Surge`}</a>{`.`}</li>
    </ul>
    <h2>{`2. Update your site metadata`}</h2>
    <p>{`After you've created the repository, you'll want to customize the information about your site. Start by replacing the site metadata in the `}<inlineCode parentName="p">{`gatsby-config.js`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`module.exports = {
  siteMetadata: {
-   title: 'Doctocat Template',
-   shortName: 'Doctocat Template',
-   header: {
-     title: 'Primer',
-     url: 'https://primer.style',
-     logoUrl: 'https://primer.style'
-   },
-   description: 'A Doctocat template site.',
+   title: 'My Site', // Used for the page titles and SEO
+   shortName: 'My Site', // Used in the header
+   header: {
+     title: 'My Home', // Used for the first link in the header. If you leave it empty ('') the link will not be rendered
+     url: 'https://www.my-home.com', // Used for the first link in the header
+     logoUrl: 'https://www.my-home.com' // Used for the link in the logo in the header
+   },
+   description: 'My site description.', // Used for SEO
  },
  ...
}
`}</code></pre>
    <p>{`Next, open the `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file and update the `}<inlineCode parentName="p">{`name`}</inlineCode>{` and `}<inlineCode parentName="p">{`repository`}</inlineCode>{` field:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`{
- "name": "doctocat-template",
- "repository": "primer/doctocat-template",
+ "name": "my-site",
+ "repository": "my-username/my-site",
  ...
}
`}</code></pre>
    <p><em parentName="p">{`Note: Doctocat uses the `}<a parentName="em" {...{
          "href": "https://docs.npmjs.com/files/package.json#repository"
        }}><inlineCode parentName="a">{`repository`}</inlineCode></a>{` field in `}<inlineCode parentName="em">{`package.json`}</inlineCode>{` as well as the `}<a parentName="em" {...{
          "href": "/theme-options"
        }}><inlineCode parentName="a">{`repoRootPath`}</inlineCode>{` theme option`}</a>{` in `}<inlineCode parentName="em">{`gatsby-config.js`}</inlineCode>{` to generate edit links for every page.`}</em></p>
    <p>{`You'll also want to update the `}<inlineCode parentName="p">{`name`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`now.json`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`{
- "name": "doctocat-template"
+ "name": "my-site"
  ...
}
`}</code></pre>
    <h2>{`3. Add your content`}</h2>
    <p>{`Next, go ahead and put your Markdown documents (`}<inlineCode parentName="p">{`.md`}</inlineCode>{` or `}<inlineCode parentName="p">{`.mdx`}</inlineCode>{`) in `}<inlineCode parentName="p">{`content/`}</inlineCode>{`. Documents in `}<inlineCode parentName="p">{`content/`}</inlineCode>{` automatically become pages with URLs based on their path relative to `}<inlineCode parentName="p">{`content/`}</inlineCode>{`. For example, if you create a `}<inlineCode parentName="p">{`content/components/box.md`}</inlineCode>{` file, Doctocat will use that file to create a `}<inlineCode parentName="p">{`/components/box`}</inlineCode>{` page.`}</p>
    <h2>{`4. Update your side navigation`}</h2>
    <p>{`Side navigation for your site is generated from the content in `}<inlineCode parentName="p">{`src/@primer/gatsby-theme-doctocat/nav.yml`}</inlineCode>{`. After you've put your documents in `}<inlineCode parentName="p">{`content/`}</inlineCode>{`, update the `}<inlineCode parentName="p">{`nav.yml`}</inlineCode>{` file accordingly:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yml"
      }}>{`- title: Example
  url: '/example'
  items:
    - title: Example 2
      url: '/example/example-2'
`}</code></pre>
    <h2>{`5. Deploy to GitHub Pages`}</h2>
    <p>{`After you've customized the content of your site, you're ready to deploy. There are many ways to deploy your site, but we currently use `}<a parentName="p" {...{
        "href": "https://pages.github.com/"
      }}>{`GitHub Pages`}</a>{` for most of our sites, and have found it to be an easy way to get sites up and running quickly.`}</p>
    <h2>{`6. Add a primer.style path alias`}</h2>
    <p>{`To make your site available from `}<inlineCode parentName="p">{`primer.style/my-site`}</inlineCode>{`, please reach out to #primer on Slack with your requirements. In the meantime, check out "`}<a parentName="p" {...{
        "href": "/usage/deployment#adding-a-primer.style-path-alias"
      }}>{`Adding a primer.style path alias`}</a>{`" in the `}<a parentName="p" {...{
        "href": "/usage/deployment"
      }}>{`Deployment`}</a>{` guide.`}</p>
    <h2>{`What's next?`}</h2>
    <p>{`Check out the `}<a parentName="p" {...{
        "href": "/usage/customization"
      }}>{`customization`}</a>{` guide.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      